import { useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, StockIndicator, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import {
  addBarcode,
  deleteBarcode,
  getInventoryItem,
  getStockLogs,
  listBarcodes,
  listSerialUnits,
  registerSerialUnits,
  updateInventoryItem,
} from "../../api/inventoryApi";
import { createBatch, listBatches } from "../../api/batchesApi";
import { deleteProductImage, listProductImages, setPrimaryImage, uploadProductImage } from "../../api/imagesApi";
import type { BarcodeOut, InventoryBatchOut, ProductImageOut, SerializedUnitOut, StockLogOut } from "../../api/types";
import { API_ROOT_URL } from "../../env";
import { canWriteInventory } from "../../roles/permissions";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { CustomFieldsEditor } from "../../components/CustomFieldsEditor";
import { LabelPrintModal } from "../../components/LabelPrintModal";
import { SupplierSelect } from "../../components/SupplierSelect";
import { TaxRateSelect } from "../../components/TaxRateSelect";
import { WriteOffModal } from "../../components/WriteOffModal";

// Guards against `/stock/new` (or `/stock/undefined`) reaching this page — a stale
// bookmark, browser autocomplete, or a manually-typed URL; the real "add product" flow is
// the create modal on InventoryListPage, not a route. Without this, every data hook below
// fired a 422 against the real API and the page rendered nothing but a raw Pydantic "Input
// should be a valid UUID" error with no way back (real bug, found in a live audit).
// Deliberately just a reserved-word check, not a full UUID-format check: this app's own
// tests render the page with human-readable mock ids (e.g. "item-1") against a mocked API
// layer that never touches the real UUID-validating backend, and those are legitimate.
const INVALID_ITEM_ID = "__invalid_item_id__";
const RESERVED_ITEM_IDS = new Set(["new", "undefined", "null"]);
function isRealItemId(id: string | undefined): id is string {
  return !!id && !RESERVED_ITEM_IDS.has(id);
}

const LOG_COLUMNS: DataTableColumn<StockLogOut, keyof StockLogOut>[] = [
  { key: "change_quantity", header: "Change", align: "right", render: (r) => r.change_quantity },
  { key: "reason", header: "Reason", render: (r) => r.reason ?? "—" },
  { key: "note", header: "Note", render: (r) => r.note ?? "—" },
  { key: "source_event_id", header: "Source Event", render: (r) => (r.source_event_id ? r.source_event_id.slice(0, 8) : "—") },
  { key: "created_at", header: "When", render: (r) => new Date(r.created_at).toLocaleString() },
];

/**
 * Item detail: the `PATCH /inventory/{id}` absolute-edit form (optimistic locking, §6.3),
 * now with category/unit_of_measure/supplier/batch_number/expiry_date editable for real
 * (§10.2 fix — these last two existed in the DB but could previously only ever be SET on
 * create, never corrected afterward), barcode management (§10.2), a write-off action
 * (item 6), and the `GET /inventory/{id}/stock-logs` audit trail. **This is the admin
 * edit path, not an offline-sync path** — never used for stock deltas originating from
 * the till or a sync replay.
 */
export function InventoryDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWriteInventory(user.role) : false;

  const itemState = useAsyncData(
    () =>
      isRealItemId(itemId)
        ? ensureFreshToken().then((token) => getInventoryItem(token, itemId))
        : Promise.reject(new Error(INVALID_ITEM_ID)),
    [itemId],
  );
  const logsState = useAsyncData(
    () =>
      isRealItemId(itemId)
        ? ensureFreshToken().then((token) => getStockLogs(token, itemId))
        : Promise.reject(new Error(INVALID_ITEM_ID)),
    [itemId],
  );

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [wholesalePrice, setWholesalePrice] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [category, setCategory] = useState("");
  const [unitOfMeasure, setUnitOfMeasure] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [taxRateId, setTaxRateId] = useState("");
  const [isEcommerceVisible, setIsEcommerceVisible] = useState(true);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [staleConflict, setStaleConflict] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);

  // Product images (business-owner request: the restock/create-product page had no way
  // to add/edit/remove images even though the backend already supported upload — see
  // `backend/app/api/v1/images.py`, now also DELETE/PATCH for real editing).
  const imagesState = useAsyncData(
    () =>
      isRealItemId(itemId)
        ? ensureFreshToken().then((token) => listProductImages(token, itemId))
        : Promise.reject(new Error(INVALID_ITEM_ID)),
    [itemId],
  );
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [pendingRemoveImage, setPendingRemoveImage] = useState<ProductImageOut | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleUploadImage(event: FormEvent) {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file || !itemState.data) return;
    setImageError(null);
    setImageUploading(true);
    try {
      const token = await ensureFreshToken();
      const hasNoImagesYet = (imagesState.data ?? []).length === 0;
      await uploadProductImage(token, itemState.data.id, file, hasNoImagesYet);
      if (fileInputRef.current) fileInputRef.current.value = "";
      imagesState.reload();
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "Could not upload image.");
    } finally {
      setImageUploading(false);
    }
  }

  async function handleSetPrimaryImage(image: ProductImageOut) {
    if (!itemState.data) return;
    setImageError(null);
    try {
      const token = await ensureFreshToken();
      await setPrimaryImage(token, itemState.data.id, image.id);
      imagesState.reload();
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "Could not set primary image.");
    }
  }

  async function handleConfirmDeleteImage() {
    if (!itemState.data || !pendingRemoveImage) return;
    setImageError(null);
    try {
      const token = await ensureFreshToken();
      await deleteProductImage(token, itemState.data.id, pendingRemoveImage.id);
      setPendingRemoveImage(null);
      imagesState.reload();
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "Could not remove image.");
    }
  }

  // Multi-batch/lot tracking — list this item's batches (FEFO order) and let a writer
  // record a manual receipt (a delivery that never went through a purchase order, or
  // backfilling a record after the fact). `TopUpStockModal`/`ScanToAddStockModal`/PO
  // receiving cover the "stock is arriving right now" flow; this panel is for viewing the
  // breakdown and the rare out-of-band correction.
  const batchesState = useAsyncData(
    () =>
      isRealItemId(itemId)
        ? ensureFreshToken().then((token) => listBatches(token, itemId))
        : Promise.reject(new Error(INVALID_ITEM_ID)),
    [itemId],
  );
  const [newBatchQty, setNewBatchQty] = useState("");
  const [newBatchNumber, setNewBatchNumber] = useState("");
  const [newBatchExpiry, setNewBatchExpiry] = useState("");
  const [newBatchUnitCost, setNewBatchUnitCost] = useState("");
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [pendingRemoveBarcode, setPendingRemoveBarcode] = useState<BarcodeOut | null>(null);

  async function handleRecordBatch(event: FormEvent) {
    event.preventDefault();
    if (!itemState.data) return;
    setBatchError(null);
    setBatchSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createBatch(token, itemState.data.id, {
        quantity: asDecimalString(newBatchQty),
        batch_number: newBatchNumber || undefined,
        expiry_date: newBatchExpiry || undefined,
        unit_cost: newBatchUnitCost ? asDecimalString(newBatchUnitCost) : undefined,
      });
      setNewBatchQty("");
      setNewBatchNumber("");
      setNewBatchExpiry("");
      setNewBatchUnitCost("");
      batchesState.reload();
      itemState.reload();
    } catch (error) {
      setBatchError(error instanceof ApiError ? error.message : "Could not record batch.");
    } finally {
      setBatchSubmitting(false);
    }
  }

  // Serialized inventory (§14.4) — register/list serial units for this item by status.
  // Mainly useful right after a PO receipt for an is_serialized item, but registration
  // works standalone here too (the backend route doesn't require a PO to exist).
  const serialUnitsState = useAsyncData(
    () =>
      isRealItemId(itemId)
        ? ensureFreshToken().then((token) => listSerialUnits(token, itemId))
        : Promise.reject(new Error(INVALID_ITEM_ID)),
    [itemId],
  );
  const [newSerials, setNewSerials] = useState("");
  const [serialSubmitting, setSerialSubmitting] = useState(false);
  const [serialError, setSerialError] = useState<string | null>(null);

  async function handleRegisterSerials(event: FormEvent) {
    event.preventDefault();
    if (!itemState.data) return;
    setSerialError(null);
    const numbers = newSerials
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (numbers.length === 0) {
      setSerialError("Enter at least one serial number (one per line, or comma-separated).");
      return;
    }
    setSerialSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await registerSerialUnits(token, itemState.data.id, { serial_numbers: numbers });
      setNewSerials("");
      serialUnitsState.reload();
      itemState.reload();
    } catch (error) {
      setSerialError(error instanceof ApiError ? error.message : "Could not register serial units.");
    } finally {
      setSerialSubmitting(false);
    }
  }

  // Barcode management. `GET /inventory/{id}/barcodes` genuinely exists (confirmed
  // 2026-07-15) — this page previously claimed otherwise and kept an in-session-only list;
  // now fetches and reflects the real persisted set.
  const barcodesState = useAsyncData(
    () =>
      isRealItemId(itemId)
        ? ensureFreshToken().then((token) => listBarcodes(token, itemId))
        : Promise.reject(new Error(INVALID_ITEM_ID)),
    [itemId],
  );
  const [newBarcode, setNewBarcode] = useState("");
  const [newBarcodePrimary, setNewBarcodePrimary] = useState(false);
  const [barcodeSubmitting, setBarcodeSubmitting] = useState(false);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [labelBarcode, setLabelBarcode] = useState<BarcodeOut | null>(null);

  // Business-owner request: "generate labels for some products with no barcodes" —
  // `POST /inventory/{id}/barcodes` already accepts any string with no format validation
  // (confirmed), so a SKU-derived code needs no new backend capability, just a one-click
  // shortcut for the common case instead of typing the SKU into "Add barcode" by hand.
  async function handleGenerateFromSku() {
    if (!itemState.data) return;
    setBarcodeError(null);
    setBarcodeSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await addBarcode(token, itemState.data.id, { code: itemState.data.sku, is_primary: (barcodesState.data ?? []).length === 0 });
      barcodesState.reload();
    } catch (error) {
      setBarcodeError(error instanceof ApiError ? error.message : "Could not generate a barcode from this SKU.");
    } finally {
      setBarcodeSubmitting(false);
    }
  }

  function startEditing() {
    if (!itemState.data) return;
    setName(itemState.data.name);
    setRetailPrice(itemState.data.retail_price);
    setWholesalePrice(itemState.data.wholesale_price ?? "");
    setLowStockThreshold(itemState.data.low_stock_threshold);
    setCategory(itemState.data.category ?? "");
    setUnitOfMeasure(itemState.data.unit_of_measure ?? "");
    setSupplierId(itemState.data.supplier_id ?? "");
    setBatchNumber(itemState.data.batch_number ?? "");
    setExpiryDate(itemState.data.expiry_date ?? "");
    setTaxRateId(itemState.data.tax_rate_id ?? "");
    setIsEcommerceVisible(itemState.data.is_ecommerce_visible);
    setCustomFields(itemState.data.custom_fields ?? {});
    setStaleConflict(false);
    setFormError(null);
    setEditing(true);
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!itemState.data) return;
    setFormError(null);
    setStaleConflict(false);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await updateInventoryItem(token, itemState.data.id, {
        name,
        retail_price: asDecimalString(retailPrice),
        wholesale_price: wholesalePrice ? asDecimalString(wholesalePrice) : null,
        low_stock_threshold: asDecimalString(lowStockThreshold),
        category: category || null,
        unit_of_measure: unitOfMeasure || null,
        supplier_id: supplierId || null,
        batch_number: batchNumber || null,
        expiry_date: expiryDate || null,
        tax_rate_id: taxRateId || null,
        is_ecommerce_visible: isEcommerceVisible,
        custom_fields: customFields,
        version: itemState.data.version,
      });
      setEditing(false);
      itemState.reload();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // Optimistic-locking conflict (§6.3, §13 rule 6): someone else (another admin
        // session, a live sale, a sync replay) updated this item since we last read its
        // `version`. Never force-overwrite — re-fetch fresh state and let the user retry
        // against it, exactly as `backend/BACKEND_ARCHITECTURE.md` prescribes.
        setStaleConflict(true);
        itemState.reload();
      } else {
        setFormError(error instanceof ApiError ? error.message : "Could not save changes.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddBarcode(event: FormEvent) {
    event.preventDefault();
    if (!itemState.data) return;
    setBarcodeError(null);
    setBarcodeSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await addBarcode(token, itemState.data.id, { code: newBarcode, is_primary: newBarcodePrimary });
      barcodesState.reload();
      setNewBarcode("");
      setNewBarcodePrimary(false);
    } catch (error) {
      setBarcodeError(error instanceof ApiError ? error.message : "Could not add barcode.");
    } finally {
      setBarcodeSubmitting(false);
    }
  }

  async function handleConfirmRemoveBarcode() {
    if (!itemState.data || !pendingRemoveBarcode) return;
    setBarcodeError(null);
    try {
      const token = await ensureFreshToken();
      await deleteBarcode(token, itemState.data.id, pendingRemoveBarcode.id);
      setPendingRemoveBarcode(null);
      barcodesState.reload();
    } catch (error) {
      setBarcodeError(error instanceof ApiError ? error.message : "Could not remove barcode.");
    }
  }

  if (itemState.error === INVALID_ITEM_ID) {
    return (
      <div className="page">
        <StatusBanner kind="error">
          That inventory item link looks invalid or out of date. To add a new product, use
          the "Add product" button on the Stock page instead.
        </StatusBanner>
        <p>
          <Link to="/stock">← Back to Stock</Link>
        </p>
      </div>
    );
  }
  if (itemState.loading) return <div className="page">Loading…</div>;
  if (itemState.error) return <div className="page"><StatusBanner kind="error">{itemState.error}</StatusBanner></div>;
  if (!itemState.data) return <div className="page">Item not found.</div>;

  const item = itemState.data;

  const barcodeColumns: DataTableColumn<BarcodeOut, string>[] = [
    { key: "code", header: "Code", render: (r) => r.code },
    { key: "is_primary", header: "Primary?", render: (r) => (r.is_primary ? "Yes" : "No") },
    {
      key: "print",
      header: "",
      render: (r) => (
        <Button variant="ghost" size="md" type="button" onClick={() => setLabelBarcode(r)}>
          Print label
        </Button>
      ),
    },
    {
      key: "remove",
      header: "",
      render: (r) =>
        canWrite ? (
          <Button variant="ghost" size="md" type="button" onClick={() => setPendingRemoveBarcode(r)}>
            Remove
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">{item.name}</h1>
          <p className="page__subtitle">
            SKU {item.sku} · version {item.version}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <StockIndicator currentStock={item.current_stock} lowStockThreshold={item.low_stock_threshold} showLabel />
          {canWrite ? (
            <Button variant="danger" onClick={() => setWriteOffOpen(true)}>
              Write off
            </Button>
          ) : null}
        </div>
      </div>

      {staleConflict ? (
        <StatusBanner kind="warning">
          This item changed since you loaded it (409 Conflict — optimistic-locking version mismatch). Reloaded the
          latest values below; review and re-apply your edit if it still applies.
        </StatusBanner>
      ) : null}

      <div className="panel">
        <div className="page__header">
          <h2 className="panel__title">Details</h2>
          {canWrite && !editing ? (
            <Button variant="secondary" onClick={startEditing}>
              Edit
            </Button>
          ) : null}
        </div>

        {editing ? (
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Input
                label="Retail price"
                inputMode="decimal"
                value={retailPrice}
                onChange={(e) => setRetailPrice(e.target.value)}
                required
              />
              <Input
                label="Wholesale price"
                inputMode="decimal"
                value={wholesalePrice}
                onChange={(e) => setWholesalePrice(e.target.value)}
                placeholder="0.00"
                hint="What you pay to stock this item — optional, used for margin reporting."
              />
              <Input
                label="Low stock threshold"
                inputMode="decimal"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value)}
                required
              />
              <Input label="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
              <Input label="Unit of measure" value={unitOfMeasure} onChange={(e) => setUnitOfMeasure(e.target.value)} />
              <SupplierSelect id="detail-supplier" label="Supplier" value={supplierId} onChange={setSupplierId} />
              <Input label="Batch number" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} />
              <Input label="Expiry date" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
              <TaxRateSelect id="detail-tax-rate" label="Tax rate" value={taxRateId} onChange={setTaxRateId} />
              <label className="sl-field">
                <span className="sl-field__label">Available online</span>
                <input
                  type="checkbox"
                  checked={isEcommerceVisible}
                  onChange={(e) => setIsEcommerceVisible(e.target.checked)}
                />
              </label>
            </div>
            <CustomFieldsEditor value={customFields} onChange={setCustomFields} />
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <dl>
            <div>
              <strong>Retail price:</strong> {item.retail_price}
            </div>
            <div>
              <strong>Wholesale price:</strong> {item.wholesale_price ?? "—"}
            </div>
            <div>
              <strong>Current stock:</strong> {item.current_stock} {item.unit_of_measure ?? ""}
            </div>
            <div>
              <strong>Low stock threshold:</strong> {item.low_stock_threshold}
            </div>
            <div>
              <strong>Category:</strong> {item.category ?? "—"}
            </div>
            <div>
              <strong>Supplier:</strong> {item.supplier_id ? <span className="muted">{item.supplier_id.slice(0, 8)}</span> : "—"}
            </div>
            <div>
              <strong>Batch:</strong> {item.batch_number ?? (item.nearest_expiry_date ? "Multiple batches — see below" : "—")}
            </div>
            <div>
              <strong>Nearest expiry:</strong> {item.nearest_expiry_date ?? "—"}
            </div>
            <div>
              <strong>Allow negative stock (backorder):</strong> {item.allow_negative_stock ? "Yes" : "No"}
            </div>
            <div>
              <strong>Tax rate:</strong> {item.tax_rate_id ? <span className="muted">{item.tax_rate_id.slice(0, 8)}</span> : "branch/tenant default"}
            </div>
            <div>
              <strong>Tracks serial numbers:</strong> {item.is_serialized ? "Yes" : "No"}
              {item.is_serialized ? (
                <span className="muted"> — each unit's serial number is recorded individually (see Serial units below)</span>
              ) : null}
            </div>
            <div>
              <strong>Available online:</strong> {item.is_ecommerce_visible ? "Yes" : "No — hidden from the storefront catalog"}
            </div>
            <div>
              <strong>Custom fields:</strong>{" "}
              {Object.keys(item.custom_fields ?? {}).length === 0
                ? "—"
                : Object.entries(item.custom_fields).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </div>
          </dl>
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">Images</h2>
        <p className="muted">
          The primary image is what shows in the POS grid and storefront catalog. Uploading replaces nothing — each
          upload adds a new image; remove one you no longer want.
        </p>
        {imageError ? <StatusBanner kind="error">{imageError}</StatusBanner> : null}
        {imagesState.error ? <StatusBanner kind="error">{imagesState.error}</StatusBanner> : null}
        {imagesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (imagesState.data ?? []).length === 0 ? (
          <p className="muted">No images yet.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 }}>
            {(imagesState.data ?? []).map((image) => (
              <div key={image.id} style={{ width: 160, textAlign: "center" }}>
                <img
                  src={`${API_ROOT_URL}${image.thumb_path}`}
                  alt={item.name}
                  style={{ width: 150, height: 150, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }}
                />
                <div style={{ marginTop: 4 }}>
                  {image.is_primary ? (
                    <span className="badge badge--current">Primary</span>
                  ) : canWrite ? (
                    <Button variant="ghost" size="md" type="button" onClick={() => void handleSetPrimaryImage(image)}>
                      Set primary
                    </Button>
                  ) : null}
                </div>
                {canWrite ? (
                  <Button variant="ghost" size="md" type="button" onClick={() => setPendingRemoveImage(image)}>
                    Remove
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {pendingRemoveImage ? (
          <div className="panel" style={{ marginBottom: 16 }}>
            <h2 className="panel__title">Remove this image?</h2>
            <p className="muted">
              {pendingRemoveImage.is_primary
                ? "This is the primary image shown in the POS grid and storefront catalog — removing it leaves the product with no primary image until you set another."
                : "This cannot be undone."}
            </p>
            <div className="form-actions">
              <Button variant="danger" type="button" onClick={() => void handleConfirmDeleteImage()}>
                Remove image
              </Button>
              <Button variant="ghost" type="button" onClick={() => setPendingRemoveImage(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
        {canWrite ? (
          <form onSubmit={handleUploadImage}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input ref={fileInputRef} type="file" accept="image/*" className="sl-input" />
              <Button type="submit" variant="secondary" disabled={imageUploading}>
                {imageUploading ? "Uploading…" : "Add image"}
              </Button>
            </div>
          </form>
        ) : null}
      </div>

      <div className="panel">
        <h2 className="panel__title">Batches</h2>
        <p className="muted">
          Different deliveries of this product can have different expiry dates and costs — each row below is one
          received lot. Sales, transfers, and write-offs draw from the soonest-expiring batch first (FEFO).
          Restocking (Top up stock / Scan to add stock / receiving a purchase order) is the normal way to add a new
          batch; use this form only for a delivery that never went through one of those.
        </p>
        {batchError ? <StatusBanner kind="error">{batchError}</StatusBanner> : null}
        {batchesState.error ? <StatusBanner kind="error">{batchesState.error}</StatusBanner> : null}
        {batchesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable<InventoryBatchOut, keyof InventoryBatchOut>
            columns={[
              { key: "batch_number", header: "Batch", render: (r) => r.batch_number ?? "—" },
              { key: "expiry_date", header: "Expiry", render: (r) => r.expiry_date ?? "—" },
              { key: "quantity_remaining", header: "Remaining", align: "right", render: (r) => r.quantity_remaining },
              { key: "quantity_received", header: "Received", align: "right", render: (r) => r.quantity_received },
              { key: "unit_cost", header: "Unit cost", align: "right", render: (r) => r.unit_cost ?? "—" },
              { key: "source", header: "Source", render: (r) => r.source },
              { key: "received_at", header: "When", render: (r) => new Date(r.received_at).toLocaleString() },
            ]}
            rows={batchesState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No batches recorded yet — stock added before this feature existed isn't tracked as a batch until it's topped up again."
          />
        )}
        {canWrite ? (
          <form onSubmit={handleRecordBatch} style={{ marginTop: 12 }}>
            <div className="form-grid">
              <Input label="Quantity" inputMode="decimal" value={newBatchQty} onChange={(e) => setNewBatchQty(e.target.value)} required />
              <Input label="Batch number" value={newBatchNumber} onChange={(e) => setNewBatchNumber(e.target.value)} placeholder="optional" />
              <Input label="Expiry date" type="date" value={newBatchExpiry} onChange={(e) => setNewBatchExpiry(e.target.value)} />
              <Input
                label="Unit cost"
                inputMode="decimal"
                value={newBatchUnitCost}
                onChange={(e) => setNewBatchUnitCost(e.target.value)}
                placeholder="optional"
              />
            </div>
            <div className="form-actions">
              <Button type="submit" variant="secondary" disabled={batchSubmitting}>
                {batchSubmitting ? "Recording…" : "Record batch manually"}
              </Button>
            </div>
          </form>
        ) : null}
      </div>

      {item.is_serialized ? (
        <div className="panel">
          <h2 className="panel__title">Serial units</h2>
          <p className="muted">
            Register serial numbers received for this item (typically right after a PO receipt) and see their status
            — <code>IN_STOCK</code>, <code>SOLD</code>, <code>RETURNED</code>, <code>DAMAGED</code>. Registering
            increments <code>current_stock</code> by the count registered.
          </p>
          {serialError ? <StatusBanner kind="error">{serialError}</StatusBanner> : null}
          {canWrite ? (
            <form onSubmit={handleRegisterSerials} style={{ marginBottom: 16 }}>
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="new-serials">
                  Serial numbers (one per line or comma-separated)
                </label>
                <textarea
                  id="new-serials"
                  className="sl-input"
                  rows={3}
                  value={newSerials}
                  onChange={(e) => setNewSerials(e.target.value)}
                  placeholder={"SN-0001\nSN-0002"}
                />
              </div>
              <div className="form-actions">
                <Button type="submit" disabled={serialSubmitting}>
                  {serialSubmitting ? "Registering…" : "Register serial units"}
                </Button>
              </div>
            </form>
          ) : null}
          {serialUnitsState.error ? <StatusBanner kind="error">{serialUnitsState.error}</StatusBanner> : null}
          {serialUnitsState.loading ? (
            <p className="muted">Loading…</p>
          ) : (
            <DataTable<SerializedUnitOut, keyof SerializedUnitOut>
              columns={[
                { key: "serial_number", header: "Serial number", render: (r) => r.serial_number },
                {
                  key: "status",
                  header: "Status",
                  render: (r) => (
                    <span className={`badge ${r.status === "IN_STOCK" ? "badge--current" : r.status === "SOLD" ? "badge--paid" : "badge--overdue"}`}>
                      {r.status}
                    </span>
                  ),
                },
                { key: "created_at", header: "Registered", render: (r) => new Date(r.created_at).toLocaleString() },
              ]}
              rows={serialUnitsState.data ?? []}
              getRowId={(row) => row.id}
              emptyMessage="No serial units registered yet."
            />
          )}
        </div>
      ) : null}

      <div className="panel">
        <h2 className="panel__title">Barcodes</h2>
        <p className="muted">
          Multi-barcode support — a product can carry more than one code (e.g. unit vs. case pack). Print a
          label for any code below (barcode + name + price, sized for sticker paper) to apply to the physical
          product and scan again at the till.
        </p>
        {barcodeError ? <StatusBanner kind="error">{barcodeError}</StatusBanner> : null}
        {barcodesState.error ? <StatusBanner kind="error">{barcodesState.error}</StatusBanner> : null}
        {barcodesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={barcodeColumns}
            rows={barcodesState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No barcodes yet."
          />
        )}
        {pendingRemoveBarcode ? (
          <div className="panel" style={{ marginTop: 12 }}>
            <h2 className="panel__title">Remove barcode {pendingRemoveBarcode.code}?</h2>
            <p className="muted">A till scan of this code will no longer find this product. This cannot be undone.</p>
            <div className="form-actions">
              <Button variant="danger" type="button" onClick={() => void handleConfirmRemoveBarcode()}>
                Remove barcode
              </Button>
              <Button variant="ghost" type="button" onClick={() => setPendingRemoveBarcode(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
        {canWrite ? (
          <>
            {(barcodesState.data ?? []).length === 0 ? (
              <div className="form-actions" style={{ marginTop: 12 }}>
                <Button type="button" variant="secondary" disabled={barcodeSubmitting} onClick={() => void handleGenerateFromSku()}>
                  {barcodeSubmitting ? "Generating…" : `Generate barcode from SKU (${item.sku})`}
                </Button>
              </div>
            ) : null}
            <form onSubmit={handleAddBarcode} style={{ marginTop: 12 }}>
              <div className="form-grid">
                <Input label="Barcode" value={newBarcode} onChange={(e) => setNewBarcode(e.target.value)} required />
                <label className="sl-field">
                  <span className="sl-field__label">Primary</span>
                  <input type="checkbox" checked={newBarcodePrimary} onChange={(e) => setNewBarcodePrimary(e.target.checked)} />
                </label>
              </div>
              <div className="form-actions">
                <Button type="submit" variant="secondary" disabled={barcodeSubmitting}>
                  {barcodeSubmitting ? "Adding…" : "Add barcode"}
                </Button>
              </div>
            </form>
          </>
        ) : null}
      </div>

      <LabelPrintModal
        open={labelBarcode !== null}
        onClose={() => setLabelBarcode(null)}
        itemName={item.name}
        priceLabel={`${item.retail_price}`}
        barcodeValue={labelBarcode?.code ?? ""}
      />

      <div className="panel">
        <h2 className="panel__title">Stock log (audit trail)</h2>
        <p className="muted">
          Every recorded stock change for this item — sales, restocks, write-offs, transfers, sync-originated
          adjustments — from
          <code> GET /inventory/{itemId}/stock-logs</code>.
        </p>
        {logsState.error ? <StatusBanner kind="error">{logsState.error}</StatusBanner> : null}
        {logsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={LOG_COLUMNS}
            rows={logsState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No stock movements recorded yet."
          />
        )}
      </div>

      <WriteOffModal
        open={writeOffOpen}
        onClose={() => setWriteOffOpen(false)}
        itemId={item.id}
        itemName={item.name}
        currentStock={item.current_stock}
        batches={batchesState.data ?? []}
        onWrittenOff={() => {
          itemState.reload();
          logsState.reload();
          batchesState.reload();
        }}
      />
    </div>
  );
}
